import { useQuery } from '@tanstack/react-query';

import type { GetGeneratePlateResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get generate plate status from a purchased vehicle
 */
export const getGeneratePlate = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetGeneratePlateResponse> => {
	const response = await apiClient.get<GetGeneratePlateResponse>(
		ordersEndpoints.generatePlate(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting generate plate status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetGeneratePlate = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.generatePlate.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get generate plate status without an order ID.');
			}
			return getGeneratePlate(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
